import {
  DocumentBlock,
  TextBlock,
  FormulaBlock,
  TableBlock,
  ImageBlock,
} from '../ast/document.types';
import { normalizeLatexFormula } from '../ast/smartLatex';
import { OCRBlock } from './ocrTypes';

function generateBlockId(type: string): string {
  return `blk-ocr-${type}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
}

/**
 * Converts reviewed OCR intermediate blocks into Canonical Document AST Blocks
 */
export function convertOCRBlocksToDocumentBlocks(ocrBlocks: OCRBlock[]): DocumentBlock[] {
  const now = new Date().toISOString();
  const documentBlocks: DocumentBlock[] = [];

  for (const block of ocrBlocks) {
    switch (block.type) {
      case 'heading': {
        const textBlock: TextBlock = {
          id: generateBlockId('h'),
          type: 'text',
          createdAt: now,
          data: {
            text: block.content,
            level: block.level === 1 ? 'heading1' : block.level === 2 ? 'heading2' : 'heading3',
            formatting: {
              bold: true,
              fontSizePt: block.level === 1 ? 16 : block.level === 2 ? 14 : 12,
            },
          },
        };
        documentBlocks.push(textBlock);
        break;
      }

      case 'text': {
        const textBlock: TextBlock = {
          id: generateBlockId('txt'),
          type: 'text',
          createdAt: now,
          data: {
            text: block.content,
            level: 'paragraph',
          },
        };
        documentBlocks.push(textBlock);
        break;
      }

      case 'formula': {
        const formulaBlock: FormulaBlock = {
          id: generateBlockId('fml'),
          type: 'formula',
          createdAt: now,
          data: {
            latex: normalizeLatexFormula(block.latex),
            displayMode: block.displayMode ?? true,
            equationNumber: block.equationNumber,
            caption: block.caption,
          },
        };
        documentBlocks.push(formulaBlock);
        break;
      }

      case 'table': {
        const rowsCount = block.rows.length + 1;
        const colsCount = Math.max(
          block.headers.length,
          ...block.rows.map((r) => r.length)
        );

        const cells = [
          block.headers.map((h, cIdx) => ({
            id: `c0_${cIdx}_${Date.now()}`,
            content: h,
            isHeader: true,
            bold: true,
            alignment: 'center' as const,
          })),
          ...block.rows.map((row, rIdx) =>
            row.map((cell, cIdx) => ({
              id: `c${rIdx + 1}_${cIdx}_${Date.now()}`,
              content: cell,
              alignment: 'center' as const,
            }))
          ),
        ];

        const tableBlock: TableBlock = {
          id: generateBlockId('tbl'),
          type: 'table',
          createdAt: now,
          data: {
            rows: rowsCount,
            cols: colsCount,
            hasHeaderRow: true,
            hasHeaderCol: false,
            borderStyle: 'academic',
            caption: block.caption,
            cells,
          },
        };
        documentBlocks.push(tableBlock);
        break;
      }

      case 'image': {
        const imageBlock: ImageBlock = {
          id: generateBlockId('img'),
          type: 'image',
          createdAt: now,
          data: {
            assetId: block.assetId || 'ocr_source',
            previewDataUrl: block.dataUrl,
            caption: block.caption || 'Extracted Experimental Figure',
            widthPercent: 80,
            alignment: 'center',
          },
        };
        documentBlocks.push(imageBlock);
        break;
      }

      case 'unknown':
      default: {
        const fallbackTextBlock: TextBlock = {
          id: generateBlockId('unk'),
          type: 'text',
          createdAt: now,
          data: {
            text: (block as any).rawContent || (block as any).content || '',
          },
        };
        documentBlocks.push(fallbackTextBlock);
        break;
      }
    }
  }

  return documentBlocks;
}
